import { useTranslation } from 'react-i18next';
import { formatEstimatedTime } from '@/services/queueService';
import type { QueueEstimation } from '@/services/queueService';
import { Clock, Users, Calendar, AlertCircle, Loader2 } from 'lucide-react';

interface QueueEstimationCardProps {
  loading: boolean;
  estimation: QueueEstimation | null;
  isOutsideWorkingHours: boolean;
  workingHoursWarning: string | null;
}

export function QueueEstimationCard({
  loading,
  estimation,
  isOutsideWorkingHours,
  workingHoursWarning,
}: QueueEstimationCardProps) {
  const { t } = useTranslation();

  // Estimated arrival time (now + estimated wait), shared by the three displays below
  let arrivalTimeLabel = '';
  if (estimation) {
    const arrivalTime = new Date();
    arrivalTime.setMinutes(arrivalTime.getMinutes() + estimation.estimatedWaitMinutes);
    arrivalTimeLabel = formatEstimatedTime(arrivalTime);
  }

  // Un cliente puede pasar ya cuando NO hay nadie delante y NO queda descanso.
  //
  // Ojo con `estimatedWaitMinutes`: no siempre es una espera. Sin nadie delante
  // son los 20 minutos de CORTESÍA para llegar (`triggers.ts:101`, y el mismo
  // número se usa como `timerExpiry`, es decir la fecha límite). Tomarlo por
  // espera lleva a decirle "ven en 20 minutos" a alguien cuyo barbero está libre.
  //
  // Antes esto era `totalInQueue === 0 || estimatedWaitMinutes <= 5`, que con la
  // cola vacía anunciaba paso inmediato aunque el barbero estuviera comiendo dos
  // horas. Y quedarse solo con `estimatedWaitMinutes <= 5` rompía el caso
  // simétrico: con la cola vacía ese valor nunca baja de 20, así que el aviso se
  // volvía inalcanzable justo cuando el barbero SÍ estaba libre.
  //
  // Por eso se mira el descanso por separado. La segunda mitad cubre el caso de
  // alguien delante a punto de terminar.
  const sinNadieDelante = estimation
    ? estimation.totalInQueue === 0 && (estimation.remainingBreakMinutes ?? 0) === 0
    : false;
  const isReadyNow = estimation
    ? sinNadieDelante || estimation.estimatedWaitMinutes <= 5
    : false;

  return loading ? (
    <div className="bg-surface border border-hairline rounded-xl p-8 mb-8">
      <div className="flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-7 h-7 text-ink-subtle animate-spin" aria-hidden="true" />
        <p className="text-sm text-ink-muted">
          {t('booking.confirmation.calculatingTime')}
        </p>
      </div>
    </div>
  ) : estimation ? (
    <div
      className={`bg-surface rounded-xl p-6 sm:p-8 mb-8 border ${
        isOutsideWorkingHours ? 'border-danger/40' : isReadyNow ? 'border-accent/50' : 'border-hairline'
      }`}
    >
      {/* Eyebrow status */}
      <div className="mb-6">
        <p className={`inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wide ${
          isOutsideWorkingHours ? 'text-danger' : isReadyNow ? 'text-accent' : 'text-ink-subtle'
        }`}>
          {isOutsideWorkingHours ? (
            <AlertCircle className="w-4 h-4" aria-hidden="true" />
          ) : (
            <Clock className="w-4 h-4" aria-hidden="true" />
          )}
          {isOutsideWorkingHours
            ? t('booking.confirmation.timeWarning')
            : isReadyNow
            ? t('booking.confirmation.comeNow')
            : t('booking.confirmation.turnInfo')}
        </p>
      </div>

      {/* Main Queue Info — two clean stats divided by a hairline */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-0 mb-6">
        {/* People Ahead */}
        <div className="md:pr-8">
          <p className="inline-flex items-center gap-1.5 text-xs text-ink-subtle mb-2 font-medium uppercase tracking-wide">
            <Users className="w-3.5 h-3.5" aria-hidden="true" />
            {t('booking.confirmation.turnsAhead')}
          </p>
          <p className="text-5xl font-semibold text-ink tabular-nums mb-1">
            {estimation.totalInQueue}
          </p>
          <p className="text-sm text-ink-muted">
            {estimation.totalInQueue === 0 ? t('booking.confirmation.youAreNext') :
             estimation.totalInQueue === 1 ? t('booking.confirmation.personWaiting') : t('booking.confirmation.peopleWaiting')}
          </p>
        </div>

        {/* Wait Time / Arrival */}
        <div className="md:pl-8 md:border-l border-hairline">
          {isReadyNow ? (
            <>
              <p className="inline-flex items-center gap-1.5 text-xs text-accent mb-2 font-medium uppercase tracking-wide">
                <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                {t('booking.confirmation.youAreNext')}
              </p>
              <p className="text-5xl font-semibold text-accent mb-1">
                {t('booking.confirmation.rightNow')}
              </p>
              <p className="text-sm text-ink-muted">
                {t('booking.confirmation.youCanComeIn')}
              </p>
            </>
          ) : (
            <>
              <p className="inline-flex items-center gap-1.5 text-xs text-ink-subtle mb-2 font-medium uppercase tracking-wide">
                <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                {t('booking.confirmation.youMustArriveAt')}
              </p>
              <p className="text-5xl font-semibold text-ink tabular-nums mb-1">
                {arrivalTimeLabel}
              </p>
              <p className="text-sm text-ink-muted">
                {t('booking.confirmation.approximately')}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Secondary Info — Estimated arrival detail */}
      <div className="bg-surface-2 rounded-lg p-5 mb-6 border border-hairline">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-ink-subtle shrink-0" aria-hidden="true" />
            <div>
              {/* Mismo criterio que arriba: manda el tiempo, no cuánta gente hay. */}
              {estimation.estimatedWaitMinutes <= 20 ? (
                <>
                  <p className="text-xs text-ink-subtle mb-0.5">
                    {t('booking.confirmation.arriveBefore')}
                  </p>
                  <p className="text-2xl font-semibold text-ink tabular-nums">
                    {arrivalTimeLabel}
                  </p>
                  <p className="text-xs text-ink-subtle mt-0.5">
                    {t('booking.confirmation.inNextMinutes', { minutes: estimation.estimatedWaitMinutes })}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-xs text-ink-subtle mb-0.5">
                    {t('booking.confirmation.arriveApproxAt')}
                  </p>
                  <p className="text-2xl font-semibold text-ink tabular-nums">
                    {arrivalTimeLabel}
                  </p>
                  <p className="text-xs text-ink-subtle mt-0.5">
                    {t('booking.confirmation.serviceFinishAt')} {formatEstimatedTime(estimation.estimatedCompletionTime)}
                  </p>
                </>
              )}
            </div>
          </div>
          <p className="text-xs text-ink-subtle md:text-right capitalize">
            {new Date().toLocaleDateString('es-ES', {
              weekday: 'long',
              day: 'numeric',
              month: 'long'
            })}
          </p>
        </div>
      </div>

      {/* Visual Queue Progress */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-ink-subtle uppercase tracking-wide font-medium">
            {t('booking.confirmation.queueProgress')}
          </span>
          <span className="text-sm font-semibold text-ink tabular-nums">
            {estimation.totalInQueue === 0 ? t('booking.confirmation.next') : `${estimation.totalInQueue} ${t('booking.confirmation.inQueue')}`}
          </span>
        </div>
        <div className="h-1.5 bg-surface-3 rounded-full overflow-hidden">
          <div
            className="h-full bg-accent rounded-full transition-all duration-700 ease-out"
            style={{
              width: estimation.totalInQueue === 0 ? '100%' : `${Math.max(10, 100 / (estimation.queuePosition + 1))}%`
            }}
          />
        </div>
      </div>

      {workingHoursWarning && (
        <div className="mt-4 bg-danger/10 border border-danger/30 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-ink">
            {workingHoursWarning}
          </p>
        </div>
      )}
    </div>
  ) : null;
}
